import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import type { MenuItemProps as MenuItem } from '@rio-cloud/rio-uikit/components/menuItems/MenuItem';
import Table, {
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableHeaderRow,
    TableRow,
} from '@rio-cloud/rio-uikit/Table';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import { type ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DiscountIdentifier } from '../../common/discount/DiscountService';
import type { AssetDiscount } from '../../common/discount/redux/types';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { ProductType } from '../../common/product/product';
import { isFreeTrialLevel } from '../../common/termedLevel/freeTrialLevel';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import { PeriodUnit, type TermedLevel } from '../../common/termedLevel/types';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { getLocalizedPeriod, getRenewsToProductLabelFromContract } from '../../common/utils/productLabelUtil';
import { ContractResourceName } from '../contracts/ContractResourceName';
import { ContractState } from '../contracts/ContractState';
import { useSorting } from '../contracts/useSorting';
import { BookingOverviewRemainingDays } from './BookingOverviewRemainingDays';
import {
    type BookingOverviewColumn,
    type BookingOverviewGrouping,
    bookingOverviewColumns,
    defaultBookingOverviewColumns,
} from './bookingOverviewColumns';

interface Props {
    contracts: TermedLevel[];
    grouping?: BookingOverviewGrouping;
    visibleColumns?: BookingOverviewColumn[];
    legacyLevelsByProduct?: ReadonlyMap<string, ReadonlySet<string>>;
    productType: ProductType;
    canConfigureRenewal: boolean;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    onConfigureRenewal: (contract: TermedLevel) => void;
    onSelectContract: (contract: TermedLevel) => void;
}

interface Column {
    id: string;
    propertyName: keyof TermedLevel;
    label: ReactNode;
}

function getColumns(productType: ProductType, grouping: BookingOverviewGrouping): Column[] {
    return bookingOverviewColumns.map(column => ({
        id: column.id,
        propertyName: column.id === 'resourceName' && grouping === 'resources' ? 'productName' : column.id,
        label:
            column.id === 'resourceName' ? (
                grouping === 'resources' ? (
                    <FormattedMessage id='intl-msg:marketplace.history.header.service' />
                ) : (
                    <ProductTypedFormattedMessage
                        productType={productType}
                        userBasedId='intl-msg:marketplace.contracts.user.table.header'
                        assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
                    />
                )
            ) : (
                <FormattedMessage id={column.labelId} />
            ),
    }));
}

function ContractPeriod({
    contract,
    discount,
    isLegacy,
}: {
    contract: TermedLevel;
    discount?: AssetDiscount;
    isLegacy: boolean;
}) {
    const intl = useIntl();
    const isFreeTrial = isFreeTrialLevel(contract.level);

    return (
        <div className='display-flex align-items-center gap-5'>
            <span className='label label-condensed label-default white-space-nowrap'>
                {contract.periodLength !== undefined && contract.periodUnit !== undefined
                    ? `${contract.periodLength} ${formatDurationUnit(contract.periodUnit, intl, contract.periodLength)}`
                    : '--'}
            </span>
            {isLegacy && <span className='label label-condensed label-info white-space-nowrap'>Legacy</span>}
            {isFreeTrial && (
                <span className='label label-condensed label-secondary white-space-nowrap'>
                    <FormattedMessage id='intl-msg:marketplace.free.trial' />
                </span>
            )}
            {!isFreeTrial && discount && (
                <span className='label label-condensed label-success white-space-nowrap'>
                    -{Math.round(discount.discountPercentage)}%
                </span>
            )}
        </div>
    );
}

function ContractDate({ value }: { value?: string }) {
    const intl = useIntl();
    return value ? intl.formatDate(parseISO(value), DATE_FORMAT) : <span>–</span>;
}

const getOverviewVariantName = (contract: TermedLevel): string => contract.variantShortName?.trim() ?? '';

function ContractVariant({ contract }: { contract: TermedLevel }) {
    const variantName = getOverviewVariantName(contract);
    return variantName ? (
        <span className='label label-condensed label-primary label-filled'>{variantName}</span>
    ) : (
        <span>–</span>
    );
}

export function BookingOverviewContractState({ contract }: { contract: TermedLevel }) {
    if (contract.hasInactiveActivationState) {
        return (
            <div className='label label-filled label-condensed label-default'>
                <FormattedMessage id='intl-msg:marketplace.bookings.overview.table.status.deleted' />
            </div>
        );
    }

    return <ContractState contractState={contract.contractState} />;
}

export function RenewalDropdown({
    contract,
    variantName,
    canConfigureRenewal,
    onConfigureRenewal,
}: {
    contract: TermedLevel;
    variantName: string;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
}) {
    const intl = useIntl();
    const periodOptions = [
        intl.formatMessage({ id: 'intl-msg:marketplace.p1d.period.label' }),
        `12 ${formatDurationUnit(PeriodUnit.MONTH, intl, 12)}`,
        `24 ${formatDurationUnit(PeriodUnit.MONTH, intl, 24)}`,
    ];
    const noRenewalOption = intl.formatMessage({ id: 'intl-msg:marketplace.contracts.noRenew' });
    const formatOption = (name: string, period: string) => (name ? `${name} (${period})` : period);
    const optionLabels =
        variantName === 'S' || variantName === 'M'
            ? [
                  noRenewalOption,
                  ...['S', 'M'].flatMap(variant => periodOptions.map(period => formatOption(variant, period))),
              ]
            : [...periodOptions.map(period => formatOption(variantName, period)), noRenewalOption];
    const items: MenuItem[] = optionLabels.map(value => ({
        value,
        onSelect: () => onConfigureRenewal(contract),
    }));
    const isRenewalConfigurable = canConfigureRenewal && isTermedLevelSelectable(contract);
    const renewalVariantName = contract.renewsToLevelVariantShortName?.trim() || undefined;
    const renewalPeriod =
        contract.renewsToLevelContractPeriodLength && contract.renewsToLevelContractPeriodUnit
            ? getLocalizedPeriod(
                  {
                      length: contract.renewsToLevelContractPeriodLength,
                      unit: contract.renewsToLevelContractPeriodUnit,
                  },
                  intl
              )
            : undefined;
    const title =
        contract.renewsToLevel === undefined ? (
            <FormattedMessage id='intl-msg:marketplace.contracts.notRenewed' />
        ) : (
            (renewalVariantName
                ? getRenewsToProductLabelFromContract(contract, intl, renewalVariantName)
                : renewalPeriod) || <span>–</span>
        );

    return (
        <div
            className={classNames({ 'cursor-not-allowed': !isRenewalConfigurable })}
            onClick={event => event.stopPropagation()}
        >
            <ButtonDropdown
                aria-label={`${intl.formatMessage({
                    id: 'intl-msg:marketplace.bookings.overview.table.renewsTo',
                })}: ${contract.resourceName ?? contract.resourceId}`}
                title={title}
                items={items}
                bsStyle='default'
                toggleButtonType='label'
                toggleClassName={classNames('label-condensed white-space-nowrap', {
                    'opacity-50': !isRenewalConfigurable,
                })}
                dropdownClassName='width-auto'
                disabled={!isRenewalConfigurable}
            />
        </div>
    );
}

function ContractTableRow({
    contract,
    productType,
    canConfigureRenewal,
    findDiscount,
    legacyLevels,
    onConfigureRenewal,
    grouping,
    visibleColumns,
}: {
    contract: TermedLevel;
    productType: ProductType;
    canConfigureRenewal: boolean;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    legacyLevels: ReadonlySet<string>;
    onConfigureRenewal: (contract: TermedLevel) => void;
    grouping: BookingOverviewGrouping;
    visibleColumns: BookingOverviewColumn[];
}) {
    const variantName = getOverviewVariantName(contract);
    const isDeleted = contract.hasInactiveActivationState;
    const discount = findDiscount({
        assetId: contract.resourceId,
        productId: contract.productId,
        productLevel: contract.level,
    });
    const values: Record<BookingOverviewColumn, ReactNode> = {
        resourceName:
            grouping === 'resources' ? (
                contract.productName
            ) : (
                <>
                    <span
                        className={classNames(
                            'uikit-icon',
                            productType === ProductType.USER_BASED ? 'icon-user' : 'icon-truck',
                            'margin-right-5'
                        )}
                        aria-hidden={true}
                    />
                    {isDeleted ? (
                        contract.resourceName || contract.resourceId
                    ) : (
                        <ContractResourceName contract={contract} />
                    )}
                </>
            ),
        variantName: <ContractVariant contract={contract} />,
        periodLength: (
            <ContractPeriod contract={contract} discount={discount} isLegacy={legacyLevels.has(contract.level)} />
        ),
        startsAt: <ContractDate value={contract.startsAt} />,
        endsAt: <ContractDate value={contract.endsAt} />,
        remainingDays: <BookingOverviewRemainingDays contract={contract} />,
        contractState: <BookingOverviewContractState contract={contract} />,
        renewsToLevelName: (
            <RenewalDropdown
                contract={contract}
                variantName={variantName}
                canConfigureRenewal={canConfigureRenewal}
                onConfigureRenewal={onConfigureRenewal}
            />
        ),
    };
    return (
        <TableRow
            {...contract}
            id={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
            className={classNames({ 'opacity-50 bg-lightest': isDeleted })}
            disabled={isDeleted}
        >
            {visibleColumns.map(column => (
                <TableColumn
                    key={column}
                    columnKey={column}
                    className={classNames('padding-x-15', { 'text-capitalize': column === 'contractState' })}
                    horizontalAlign={column === 'renewsToLevelName' ? 'right' : 'left'}
                    preventRowClick={column === 'renewsToLevelName'}
                >
                    {values[column]}
                </TableColumn>
            ))}
        </TableRow>
    );
}

export function BookingOverviewContractTable({
    contracts,
    grouping = 'services',
    visibleColumns = defaultBookingOverviewColumns,
    legacyLevelsByProduct,
    productType,
    canConfigureRenewal,
    findDiscount,
    legacyLevels,
    onConfigureRenewal,
    onSelectContract,
}: Props) {
    const { sort, handleSortChange, sortBy, direction } = useSorting<TermedLevel>(
        grouping === 'resources' ? 'productName' : 'resourceName'
    );
    const columns = useMemo(
        () =>
            getColumns(productType, grouping).filter(column =>
                visibleColumns.includes(column.id as BookingOverviewColumn)
            ),
        [productType, grouping, visibleColumns]
    );

    return (
        <Table
            rowKey='id'
            hover
            border={false}
            rounded={false}
            className='rounded-bottom'
            sortBy={columns.find(column => column.propertyName === sortBy)?.id}
            sortDirection={direction}
            onSortChange={columnKey => {
                const column = columns.find(({ id }) => id === columnKey);
                if (column) {
                    handleSortChange(column.propertyName);
                }
            }}
            onRowClick={contract => onSelectContract(contract as unknown as TermedLevel)}
        >
            <TableHeader>
                <TableHeaderRow>
                    {columns.map(column => (
                        <TableHeaderColumn
                            key={column.id}
                            columnKey={column.id}
                            className='padding-x-15'
                            horizontalAlign={column.id === 'renewsToLevelName' ? 'right' : 'left'}
                            sortable
                        >
                            {column.label}
                        </TableHeaderColumn>
                    ))}
                </TableHeaderRow>
            </TableHeader>
            <TableBody maxHeight={290}>
                {sort(contracts).map(contract => (
                    <ContractTableRow
                        key={`${contract.productId}-${contract.resourceId}-${contract.level}-${contract.createdAt}`}
                        contract={contract}
                        productType={productType}
                        canConfigureRenewal={canConfigureRenewal}
                        findDiscount={findDiscount}
                        legacyLevels={legacyLevelsByProduct?.get(contract.productId) ?? legacyLevels}
                        grouping={grouping}
                        visibleColumns={visibleColumns}
                        onConfigureRenewal={onConfigureRenewal}
                    />
                ))}
            </TableBody>
        </Table>
    );
}
